import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskBadge } from "@/components/StatusBadge";
import { timeAgo, fmtDateTime } from "@/lib/format";
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, User, ArrowRight, TriangleAlert,
  MessageSquare, StickyNote, Pencil, Plus, Save, X, Check,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_STYLE = {
  "Clear": { fg: "#059669", bg: "#ECFDF5" },
  "Needs Attention": { fg: "#D97706", bg: "#FFFBEB" },
  "Update Due": { fg: "#D97706", bg: "#FFFBEB" },
};
const fieldCls = "h-9 w-full px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand";

function CustomerStatus({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE["Clear"];
  return (
    <span data-testid={`profile-status-${status?.toLowerCase().replace(/\s+/g, "-")}`}
      className="inline-flex items-center rounded-sm font-semibold border px-2.5 py-1 text-xs"
      style={{ color: s.fg, backgroundColor: s.bg, borderColor: s.fg }}>
      {status}
    </span>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "danger" && value > 0 ? "#DC2626" : tone === "warn" && value > 0 ? "#D97706" : tone === "info" && value > 0 ? "#0B132B" : tone === "ok" && value > 0 ? "#059669" : "#0B132B";
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-surface-alt border border-line rounded-sm px-3 py-2.5">
      <div className="font-mono-data text-[20px] font-semibold leading-none" style={{ color }}>{value}</div>
      <div className="op-label mt-1.5">{label}</div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="min-w-0">
        <div className="op-label">{label}</div>
        <div className="text-[13.5px] text-ink mt-0.5 break-words">{children || "—"}</div>
      </div>
    </div>
  );
}

function EditCustomerDialog({ customer, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(customer);
  useEffect(() => { setForm(customer); }, [customer]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Customer name is required.");
    try {
      const r = await client.put(`/customers/${customer.id}`, {
        name: form.name, contact_person: form.contact_person, email: form.email, phone: form.phone, address: form.address,
      });
      toast.success("Customer updated");
      onOpenChange(false);
      onSaved(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update customer.");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Customer</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">Update contact details for this customer.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5 py-1">
          <div><label className="op-label block mb-1.5">Customer name</label><input data-testid="edit-customer-name" className={fieldCls} value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><label className="op-label block mb-1.5">Primary contact</label><input data-testid="edit-customer-contact" className={fieldCls} value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="op-label block mb-1.5">Email</label><input data-testid="edit-customer-email" className={fieldCls} value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className="op-label block mb-1.5">Phone</label><input data-testid="edit-customer-phone" className={fieldCls} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          <div><label className="op-label block mb-1.5">Address</label><input data-testid="edit-customer-address" className={fieldCls} value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <button data-testid="edit-customer-save" onClick={submit} className="h-9 px-4 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-dark transition-colors flex items-center gap-1.5"><Save className="h-4 w-4" /> Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, title, count, action, children }) {
  return (
    <div className="bg-surface border border-line rounded-md overflow-hidden">
      <div className="px-5 py-3.5 border-b border-line flex items-center gap-2.5">
        <h3 className="font-heading text-[15px] font-semibold text-ink">{title}</h3>
        {count != null && <span className="font-mono-data text-[11px] text-ink-3">{count}</span>}
        {action && <div className="ml-auto">{action}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEditNotes = user?.role === "admin" || user?.role === "manager";
  const [c, setC] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");

  const load = () => client.get(`/customers/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); }, [id]);

  if (!c) return <Layout><div className="text-sm text-ink-3 font-mono-data py-20 text-center">Loading customer…</div></Layout>;

  const s = c.summary || {};
  const activeJobs = (c.jobs || []).filter((j) => j.current_stage !== "Closed");

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await client.post(`/customers/${id}/notes`, { body: newNote });
      setNewNote("");
      load();
      toast.success("Note added");
    } catch (e) { toast.error("Could not add note."); }
  };
  const saveNote = async (nid) => {
    if (!editNoteText.trim()) return;
    try {
      await client.put(`/customers/${id}/notes/${nid}`, { body: editNoteText });
      setEditingNote(null);
      load();
      toast.success("Note updated");
    } catch (e) { toast.error("Could not update note."); }
  };

  return (
    <Layout>
      <button data-testid="back-to-customers" onClick={() => navigate("/app/customers")} className="text-[13px] text-ink-2 hover:text-brand flex items-center gap-1.5 mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> All Customers
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <p className="op-label mb-1">Customer profile</p>
          <h1 className="font-heading text-2xl font-semibold text-ink">{c.name}</h1>
          <div className="mt-2"><CustomerStatus status={c.status_indicator} /></div>
        </div>
        {isAdmin && (
          <button data-testid="edit-customer-btn" onClick={() => setEditOpen(true)} className="h-9 px-3.5 rounded-sm border border-line bg-surface text-[13px] font-medium text-ink-2 hover:border-steel hover:text-ink flex items-center gap-1.5 transition-colors">
            <Pencil className="h-3.5 w-3.5" /> Edit Customer
          </button>
        )}
      </div>

      {/* Operational summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 mb-5">
        <Stat label="Active" value={s.active_jobs ?? 0} />
        <Stat label="Delayed" value={s.delayed_jobs ?? 0} tone="danger" />
        <Stat label="Missing Docs" value={s.missing_doc_jobs ?? 0} tone="warn" />
        <Stat label="Updates Due" value={s.customer_updates_due ?? 0} tone="info" />
        <Stat label="Ready to Close" value={s.ready_to_close_jobs ?? 0} tone="ok" />
        <Stat label="Closed" value={s.closed_jobs ?? 0} />
        <Stat label="Total Jobs" value={s.total_jobs ?? 0} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left column: Overview */}
        <div className="space-y-5">
          <Section icon={User} title="Overview">
            <div className="space-y-4">
              <Field icon={User} label="Primary contact">{c.contact_person}</Field>
              <Field icon={Mail} label="Email">{c.email}</Field>
              <Field icon={Phone} label="Phone">{c.phone}</Field>
              <Field icon={MapPin} label="Address">{c.address}</Field>
              <Field icon={MessageSquare} label="Last update sent">{c.last_update_sent ? timeAgo(c.last_update_sent) : "No updates yet"}</Field>
            </div>
          </Section>

          {/* Internal notes */}
          <Section icon={StickyNote} title="Internal Notes" count={(c.note_log || []).length}>
            <div className="space-y-3">
              {(c.note_log || []).length === 0 && <p className="text-[13px] text-ink-3">No notes yet.</p>}
              {(c.note_log || []).map((n) => (
                <div key={n.id} data-testid={`note-${n.id}`} className="border border-line rounded-sm p-3 bg-surface-alt">
                  {editingNote === n.id ? (
                    <div>
                      <textarea data-testid="note-edit-input" rows={2} value={editNoteText} onChange={(e) => setEditNoteText(e.target.value)}
                        className="w-full rounded-sm border border-line bg-surface p-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-brand resize-none" />
                      <div className="flex justify-end gap-2 mt-2">
                        <button onClick={() => setEditingNote(null)} className="text-[12px] text-ink-3 hover:text-ink flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancel</button>
                        <button data-testid="note-save-btn" onClick={() => saveNote(n.id)} className="text-[12px] text-brand font-medium flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Save</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[13px] text-ink whitespace-pre-wrap">{n.body}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[11px] text-ink-3 font-mono-data">{n.author} · {n.updated_at ? `edited ${timeAgo(n.updated_at)}` : timeAgo(n.created_at)}</span>
                        {canEditNotes && (
                          <button data-testid={`edit-note-${n.id}`} onClick={() => { setEditingNote(n.id); setEditNoteText(n.body); }} className="text-ink-3 hover:text-brand"><Pencil className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input data-testid="new-note-input" className={fieldCls} placeholder="Add a note…" value={newNote} onChange={(e) => setNewNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <button data-testid="add-note-btn" onClick={addNote} className="h-9 px-3 rounded-sm bg-brand text-white text-[13px] font-medium hover:bg-brand-dark flex items-center gap-1 shrink-0"><Plus className="h-3.5 w-3.5" /> Add</button>
            </div>
          </Section>
        </div>

        {/* Right columns */}
        <div className="lg:col-span-2 space-y-5">
          {/* Open issues */}
          <Section icon={TriangleAlert} title="Open Issues" count={(c.open_issues || []).length}>
            {(c.open_issues || []).length === 0 ? (
              <p className="text-[13px] text-ink-3">No open issues — this customer is clear.</p>
            ) : (
              <div className="space-y-2">
                {c.open_issues.map((j) => (
                  <button key={j.id} data-testid={`issue-${j.job_number}`} onClick={() => navigate(`/app/shipments/${j.id}`)}
                    className="w-full text-left border border-line rounded-sm p-3 hover:bg-surface-alt transition-colors flex items-start justify-between gap-3 group">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-data text-[12.5px] text-ink font-medium">{j.job_number}</span>
                        <span className="text-[11.5px] text-ink-3">{j.current_stage}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(j.issues || []).map((i, idx) => (
                          <span key={idx} className="text-[11px] px-1.5 py-0.5 rounded-sm border border-warn/50 bg-warn/10 text-warn font-medium">{i}</span>
                        ))}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </Section>

          {/* Active jobs */}
          <Section icon={ArrowRight} title="Active Jobs" count={activeJobs.length}>
            {activeJobs.length === 0 ? (
              <p className="text-[13px] text-ink-3">No active jobs for this customer.</p>
            ) : (
              <div className="overflow-x-auto mv-scroll -mx-5 -my-5">
                <table className="w-full text-left">
                  <thead><tr className="border-b border-line bg-surface-alt">
                    {["Job #", "Stage", "Owner", "Risk", ""].map((h) => <th key={h} className="op-label px-5 py-2.5 whitespace-nowrap font-semibold">{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {activeJobs.map((j) => (
                      <tr key={j.id} data-testid={`active-job-${j.job_number}`} onClick={() => navigate(`/app/shipments/${j.id}`)} className="hover:bg-surface-alt transition-colors cursor-pointer group">
                        <td className="px-5 py-3 font-mono-data text-[12.5px] text-ink font-medium whitespace-nowrap">{j.job_number}</td>
                        <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.current_stage}</td>
                        <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.assigned_owner}</td>
                        <td className="px-5 py-3 whitespace-nowrap"><RiskBadge status={j.risk_status} /></td>
                        <td className="px-5 py-3"><ArrowRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Customer updates */}
          <Section icon={MessageSquare} title="Customer Updates" count={(c.customer_updates || []).length}>
            {(c.customer_updates || []).length === 0 ? (
              <p className="text-[13px] text-ink-3">No customer updates sent yet.</p>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-y-auto mv-scroll">
                {c.customer_updates.map((u) => (
                  <div key={u.id} data-testid={`update-${u.id}`} className="border-l-2 border-steel/40 pl-3 py-0.5">
                    <p className="text-[13px] text-ink">{u.message}</p>
                    <div className="text-[11px] text-ink-3 font-mono-data mt-1">
                      <span className="text-ink-2">{u.job_number}</span> · {u.sent_by} · {fmtDateTime(u.sent_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      <EditCustomerDialog customer={c} open={editOpen} onOpenChange={setEditOpen} onSaved={() => load()} />
    </Layout>
  );
}
